import * as React from 'react'
import cn from 'classnames'
import { observer } from 'mobx-react-lite'
import { Avatar, Dropdown } from 'src/design-system'
import { store } from 'store/index'
import { fadeOutAndVisit } from 'app/packs/src/utils/fade-out-visit'
import { RailsActionForm } from 'components/rails-form-button/rails-form-button'

export type AccountMenuProps = {
  /** Icon-only, for a collapsed rail. */
  collapsed?: boolean
}

/**
 * Who you are signed in as, and how to leave.
 *
 * Shared by both shells on purpose: the main app and the WFI island each have
 * their own sidebar, but the account row is the same thing in both, and the
 * design draws it once. Its styling lives in `.cm-account` in
 * stylesheets/base/_careerminds-brand.scss rather than in either sidebar's
 * module, so the two cannot drift apart.
 *
 * Settings leave the WFI island, so they are full-page visits in both shells.
 */
export const AccountMenu = observer(
  ({ collapsed = false }: AccountMenuProps) => {
    const [open, setOpen] = React.useState(false)
    const user = store.currentUser
    // Bound out of the observable so the click handlers keep the non-null
    // narrowing the JSX guard establishes.
    const org = user?.org

    if (!user) return null

    return (
      <Dropdown.Root onOpenChange={setOpen} open={open}>
        <Dropdown.Trigger
          className={cn('cm-account', collapsed && 'cm-account--collapsed')}
          hideCarat={collapsed}
          data-element-id="side-nav-account"
          title={user.fullName}
        >
          <Avatar
            fallbackText={user.initials}
            src={user.avatarUrl}
            alt={`Avatar for ${user.fullName}`}
            size="sm"
          />
          {!collapsed && (
            <span className="cm-account__text">
              <span className="cm-account__name">{user.fullName}</span>
              <span className="cm-account__sub">{org?.name}</span>
            </span>
          )}
        </Dropdown.Trigger>
        <Dropdown.Content
          align="start"
          className="cm-account__menu"
          side="top"
          sideOffset={6}
        >
          <Dropdown.Button
            className="cm-account__item"
            onClick={() => fadeOutAndVisit('/users/edit')}
          >
            Profile settings
          </Dropdown.Button>
          {user.isAdmin && org && (
            <Dropdown.Button
              className="cm-account__item"
              onClick={() => fadeOutAndVisit(`/orgs/${org.slug}/edit`)}
            >
              {org.name} settings
            </Dropdown.Button>
          )}
          <RailsActionForm
            method="delete"
            action="/users/logout"
            formMethod="post"
          >
            <button
              type="submit"
              className="cm-account__item cm-account__item--danger"
            >
              Log out
            </button>
          </RailsActionForm>
        </Dropdown.Content>
      </Dropdown.Root>
    )
  },
)
